import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import InfoTipLabel from './InfoTip';

type Props = {
  width?: number;
  height?: number;
  disabled?: boolean;
  label?: string;
  onDirtyChange?: (hasInk: boolean) => void;
};

export type SignaturePadHandle = {
  clear: () => void;
  isEmpty: () => boolean;
  toDataURL: (type?: string) => string;
  toBlob: () => Promise<Blob | null>;
};

type Point = { x: number; y: number };

/**
 * Mouse, trackpad, stylus, and touch. Sizing the canvas is isolated from
 * parent re-renders so the first stroke is not wiped.
 */
const SignaturePad = React.forwardRef<SignaturePadHandle, Props>(function SignaturePad(
  { width = 360, height = 140, disabled = false, label = 'Draw signature', onDirtyChange },
  ref
) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);
  const dirtyCb = useRef(onDirtyChange);
  dirtyCb.current = onDirtyChange;
  const [ink, setInk] = useState(false);

  const setHasInk = useCallback((v: boolean) => {
    if (hasInk.current === v) return;
    hasInk.current = v;
    setInk(v);
    dirtyCb.current?.(v);
  }, []);

  const canvasPoint = (clientX: number, clientY: number): Point | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return {
      x: ((clientX - rect.left) / rect.width) * canvas.width,
      y: ((clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const paintDot = (ctx: CanvasRenderingContext2D, p: Point) => {
    ctx.beginPath();
    ctx.arc(p.x, p.y, 1.6, 0, Math.PI * 2);
    ctx.fill();
  };

  const startStroke = (clientX: number, clientY: number) => {
    if (disabled) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const p = canvasPoint(clientX, clientY);
    if (!canvas || !ctx || !p) return;
    drawing.current = true;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
    ctx.fillStyle = '#111827';
    ctx.lineWidth = Math.max(2.4, canvas.width / 140);
    paintDot(ctx, p);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    setHasInk(true);
  };

  const moveStroke = (clientX: number, clientY: number) => {
    if (!drawing.current || disabled) return;
    const ctx = canvasRef.current?.getContext('2d');
    const p = canvasPoint(clientX, clientY);
    if (!ctx || !p) return;
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const endStroke = () => {
    drawing.current = false;
  };

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
  }, [setHasInk]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.max(1, Math.floor(width * dpr));
    canvas.height = Math.max(1, Math.floor(height * dpr));
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    hasInk.current = false;
    setInk(false);
  }, [width, height]);

  useImperativeHandle(
    ref,
    () => ({
      clear,
      isEmpty: () => !hasInk.current,
      toDataURL: (type = 'image/png') => canvasRef.current?.toDataURL(type) || '',
      toBlob: () =>
        new Promise((resolve) => {
          const canvas = canvasRef.current;
          if (!canvas || !hasInk.current) {
            resolve(null);
            return;
          }
          canvas.toBlob((b) => resolve(b), 'image/png');
        }),
    }),
    [clear]
  );

  return (
    <Box sx={{ width: '100%', maxWidth: width }}>
      {label && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
      )}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          bgcolor: '#fff',
          overflow: 'hidden',
          position: 'relative',
          touchAction: 'none',
          userSelect: 'none',
          cursor: disabled ? 'default' : 'crosshair',
        }}
      >
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          onPointerDown={(e) => {
            if (disabled) return;
            e.preventDefault();
            e.stopPropagation();
            try {
              e.currentTarget.setPointerCapture(e.pointerId);
            } catch {
              /* ignore */
            }
            startStroke(e.clientX, e.clientY);
          }}
          onPointerMove={(e) => {
            if (!drawing.current) return;
            e.preventDefault();
            moveStroke(e.clientX, e.clientY);
          }}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
          onMouseDown={(e) => {
            if (disabled) return;
            e.preventDefault();
            e.stopPropagation();
            startStroke(e.clientX, e.clientY);
          }}
          onMouseMove={(e) => {
            if (!drawing.current) return;
            e.preventDefault();
            moveStroke(e.clientX, e.clientY);
          }}
          onMouseUp={endStroke}
          onMouseLeave={endStroke}
          style={{
            display: 'block',
            width: '100%',
            height: `${height}px`,
            touchAction: 'none',
            cursor: disabled ? 'default' : 'crosshair',
          }}
        />
        {!ink && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: '42%',
              textAlign: 'center',
              pointerEvents: 'none',
              userSelect: 'none',
            }}
          >
            Sign here — mouse, trackpad, finger, or stylus
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.75 }}>
        <Button size="small" onClick={clear} disabled={disabled || !ink} sx={{ textTransform: 'none' }}>
          <InfoTipLabel tip="Erases what you drew in the box so you can sign again.">Clear</InfoTipLabel>
        </Button>
      </Box>
    </Box>
  );
});

export default SignaturePad;
